import { useEffect } from 'react';

import { BRAND_COLORS } from './brandTokens';

/**
 * The browser-sized R mark. It deliberately uses the same 100-unit geometry
 * and five-colour system as RichardMonogram rather than a generic admin
 * favicon, so an askRichard or Lens tab remains recognisable among several
 * open tabs.
 */
const faviconSvg = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100"><circle cx="50" cy="50" r="50" fill="${BRAND_COLORS.oceanTeal}"/><g transform="translate(24,24) scale(.104)" fill="${BRAND_COLORS.compassWhite}" fill-rule="evenodd"><path d="M9.1 9.1h311.1v149.5H9.1z"/><path d="m342.8 9.1 1.6 1.5 24.1 1.4 28.7 9.1 27.2 14.8 27.2 24.2 14.5 31.8 9.1 32.8 1.9 34.8v148.6H341.3V10.6z"/><path d="M9.1 179.7h149.5v149.6H9.1z"/><path d="M179.7 179.7h308.1v10.6l-7.8 36.2-14.9 31.6-25.7 30.4-27.3 19.6-18.1 9.1-28.7 8.6-22.5 1.6-1.5 1.6H179.7z"/><path d="m392.7 342.8 11.3 11.2 6.8 10 5 10 90.6 124.4H356.4l-20.8-10.9-7.9-9.3-68-128h88z"/><path d="M9.1 350.4h149.5v148H9.1z"/></g></svg>`;

export const ASK_RICHARD_FAVICON_HREF = `data:image/svg+xml,${encodeURIComponent(faviconSvg)}`;

/**
 * Swaps the app-wide fallback favicon only while an askRichard or Lens route
 * is mounted, then restores every existing icon link exactly as it found it.
 * This keeps unrelated admin routes on their own established browser mark.
 */
export default function AskRichardFavicon() {
  useEffect(() => {
    const existing = Array.from(document.querySelectorAll<HTMLLinkElement>('link[rel~="icon"]'));
    const links = existing.length ? existing : [Object.assign(document.createElement('link'), { rel: 'icon' })];
    const created = existing.length === 0 ? links[0] : null;
    if (created) document.head.appendChild(created);

    const previous = links.map((link) => ({
      href: link.getAttribute('href'),
      sizes: link.getAttribute('sizes'),
      type: link.getAttribute('type'),
    }));

    for (const link of links) {
      link.setAttribute('href', ASK_RICHARD_FAVICON_HREF);
      link.setAttribute('type', 'image/svg+xml');
      link.removeAttribute('sizes');
    }

    return () => {
      for (const [index, link] of links.entries()) {
        const original = previous[index];
        if (original.href === null) link.removeAttribute('href');
        else link.setAttribute('href', original.href);
        if (original.type === null) link.removeAttribute('type');
        else link.setAttribute('type', original.type);
        if (original.sizes === null) link.removeAttribute('sizes');
        else link.setAttribute('sizes', original.sizes);
      }
      created?.remove();
    };
  }, []);

  return null;
}
